/**
 * Identical requests share one answer (LCP fixes 24.09): the translation backend (namespace
 * templates) and the entity store (collection reads, e.g. the shop's product list) both use it.
 * A failed request is never kept; `keepSettledMs` keeps a settled answer for that long (0 = only
 * while the request is in flight).
 */
export interface SharedRequests<T> {
    get(key: string, load: () => Promise<T>): Promise<T>;
    clear(): void;
}

export function createSharedRequests<T>(
    keepSettledMs = 0,
    isFailure: (value: T) => boolean = () => false
): SharedRequests<T> {
    const entries = new Map<string, { promise: Promise<T>; settledAt?: number }>();

    return {
        get(key, load) {
            const entry = entries.get(key);
            if (entry && (entry.settledAt === undefined || Date.now() - entry.settledAt < keepSettledMs)) {
                return entry.promise;
            }
            const promise = load();
            const created = { promise } as { promise: Promise<T>; settledAt?: number };
            entries.set(key, created);
            promise.then(
                value => {
                    if (entries.get(key) !== created) return;
                    if (keepSettledMs > 0 && !isFailure(value)) created.settledAt = Date.now();
                    else entries.delete(key);
                },
                () => {
                    if (entries.get(key) === created) entries.delete(key);
                }
            );
            return promise;
        },
        clear() {
            entries.clear();
        },
    };
}
